import useStaffTimelineOriginalActions from "../../features/staffNotes/useStaffTimelineOriginalActions";
import useStaffOriginalProtection from "./hooks/useStaffOriginalProtection";
import React from "react";
import { Box } from "@mui/material";
import TimelineEntryRow from "./parts/TimelineEntryRow.jsx";
import { transformTimelineEntry } from "./entryTransformers";

const UnifiedTimelineEntry = ({
  entry,
  presentation,
  timeString,
  isLast = false,
  showActions = false,
  onEditEntry = null,
  onDeleteEntry = null,
  actionMenuDisabled = false,
  isEditing = false,
  editText = "",
  onEditTextChange = null,
  onSaveEdit = null,
  onCancelEdit = null,
  layoutVariant = "default",
  caregiverLabel = "",
  caregiverRole = null,
  currentUserRole = null,
  showCaptureFirstOriginalNote = true,
  showCaptureFirstContextDetails = true,
  suppressPendingMedicationReviewAction = false,
  sourceDetailsNode = null,
  storyParentExpanded = false,
  onStoryParentToggle = null,
  child = null,
  nested = false,
}) => {
  const entryConfig = React.useMemo(
    () => transformTimelineEntry(entry, presentation || {}, timeString, child),
    [child, entry, presentation, timeString],
  );

  const childId = child?.id || entry.childId;
  const protection = useStaffOriginalProtection(entry, childId);
  const originalActions = useStaffTimelineOriginalActions({
    protection,
    childId,
    child,
    currentUserRole,
  });
  const protectedOriginal =
    protection.blocked || (protection.staff && !originalActions.canMutate);
  const edit = protection.staff
    ? originalActions.canMutate && onEditEntry
      ? originalActions.onEdit
      : null
    : !protectedOriginal && onEditEntry
      ? () => onEditEntry(entry, entryConfig)
      : null;
  const remove = protection.staff
    ? originalActions.canMutate && onDeleteEntry
      ? originalActions.onDelete
      : null
    : protectedOriginal
      ? null
      : onDeleteEntry;

  return (
    <Box sx={{ width: "100%" }}>
      <TimelineEntryRow
        entryId={entry.id}
        entryData={entry}
        entryConfig={entryConfig}
        isLast={isLast}
        showActions={showActions}
        onDeleteEntry={remove}
        actionMenuDisabled={actionMenuDisabled || originalActions.busy}
        isEditing={isEditing}
        editValue={editText}
        onEditValueChange={onEditTextChange}
        onEditEntry={edit}
        onEditOriginal={protection.staff ? edit : null}
        onSaveEdit={() => onSaveEdit?.(entry)}
        onCancelEdit={onCancelEdit}
        layoutVariant={layoutVariant}
        nested={nested}
        caregiverLabel={caregiverLabel}
        caregiverRole={caregiverRole}
        currentUserRole={currentUserRole}
        showCaptureFirstOriginalNote={showCaptureFirstOriginalNote}
        showCaptureFirstContextDetails={showCaptureFirstContextDetails}
        suppressPendingMedicationReviewAction={
          suppressPendingMedicationReviewAction
        }
        sourceDetailsNode={sourceDetailsNode}
        storyParentExpanded={storyParentExpanded}
        onStoryParentToggle={onStoryParentToggle}
      />
      {originalActions.editor}
    </Box>
  );
};

export default React.memo(UnifiedTimelineEntry);
